import React from "react";
import { Box } from "@mui/material";
import {
  certificatePageSize,
  certificatePaperColour,
  type CertificateOrientation,
} from "../../../domain/certificates/certificateTemplates";
import { CentredLayout, type CertificateDesign } from "./certificateParts";
import { pointsAround, starPoints, type Point } from "./ornamentShapes";

/** The shapes run round the page this far in from its edge, outside the border. */
const SHAPE_LINE = 24;
/** About how far apart the shapes sit, and how big each one is. */
const SHAPE_SPACING = 64;
const SHAPE_SIZE = 22;
/** The thick rounded border, just inside the shapes. */
const BORDER_INSET = 46;

/** A round area of the page: the starburst, and the space kept clear for it. */
type Circle = Point & { radius: number };

/**
 * Where the starburst sits and how big it is: over the top-left corner of the
 * border on a landscape page, and above the text on a portrait one. The text
 * column's inset keeps clear of it.
 */
function starburstFor(
  orientation: CertificateOrientation,
  width: number,
): Circle {
  return orientation === "landscape"
    ? { x: 96, y: 96, radius: 52 }
    : { x: width / 2, y: 104, radius: 58 };
}

const HALF = SHAPE_SIZE / 2;

/** The shapes round the edge, in turn, each centred on the origin and SHAPE_SIZE across. */
const SHAPES: ((colour: string) => React.ReactElement)[] = [
  // Dot
  (colour) => <circle r={HALF} fill={colour} />,
  // Triangle
  (colour) => (
    <polygon
      points={`0,${-HALF} ${HALF},${HALF * 0.8} ${-HALF},${HALF * 0.8}`}
      fill={colour}
    />
  ),
  // Rounded square
  (colour) => (
    <rect
      x={-HALF * 0.8}
      y={-HALF * 0.8}
      width={HALF * 1.6}
      height={HALF * 1.6}
      rx={3}
      fill={colour}
    />
  ),
  // Star
  (colour) => (
    <polygon points={starPoints(5, HALF, HALF * 0.45)} fill={colour} />
  ),
  // Ring
  (colour) => (
    <circle r={HALF * 0.75} fill="none" stroke={colour} strokeWidth={4} />
  ),
  // Zigzag
  (colour) => (
    <polyline
      points={`${-HALF},3 ${-HALF / 2},-4 0,3 ${HALF / 2},-4 ${HALF},3`}
      fill="none"
      stroke={colour}
      strokeWidth={4}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  ),
];

/** Bright shapes all round the page, leaving room for the starburst. */
const ShapeBorder: React.FC<{
  width: number;
  height: number;
  colours: string[];
  keepClearOf: Circle;
}> = ({ width, height, colours, keepClearOf }) => {
  const shapes = pointsAround(width, height, SHAPE_LINE, SHAPE_SPACING).filter(
    (point) =>
      Math.hypot(point.x - keepClearOf.x, point.y - keepClearOf.y) >
      keepClearOf.radius + SHAPE_SIZE,
  );
  return (
    <Box
      component="svg"
      viewBox={`0 0 ${width} ${height}`}
      aria-hidden="true"
      sx={{ position: "absolute", inset: 0, width, height }}
    >
      {shapes.map((point, i) => (
        <g
          key={i}
          // A different tilt for each, so they look scattered.
          transform={`translate(${point.x} ${point.y}) rotate(${(i * 47) % 360})`}
        >
          {SHAPES[i % SHAPES.length](
            // Swap colours each time round the shapes, so each comes in both.
            colours[(i + Math.floor(i / SHAPES.length)) % colours.length],
          )}
        </g>
      ))}
    </Box>
  );
};

/** A spiky starburst badge with a ring and a star at its middle, no text. */
const Starburst: React.FC<
  Circle & { primary: string; accent: string; paper: string }
> = ({ x, y, radius, primary, accent, paper }) => (
  <Box
    component="svg"
    viewBox={`${-radius} ${-radius} ${radius * 2} ${radius * 2}`}
    aria-hidden="true"
    sx={{
      position: "absolute",
      left: x - radius,
      top: y - radius,
      width: radius * 2,
      height: radius * 2,
    }}
  >
    <polygon points={starPoints(18, radius, radius * 0.78)} fill={accent} />
    <circle r={radius * 0.66} fill={primary} />
    <circle
      r={radius * 0.56}
      fill="none"
      stroke={paper}
      strokeWidth={2}
      strokeDasharray="4 4"
    />
    <polygon
      points={starPoints(5, radius * 0.38, radius * 0.16)}
      fill={paper}
    />
  </Box>
);

/**
 * Playful: bright shapes all round a thick rounded border, and a starburst,
 * like a children's course certificate.
 */
export const playfulDesign: CertificateDesign = {
  bodyFont: "nunito",
  Layout: ({ template, text, orientation }) => {
    const { primaryColour: primary, accentColour: accent } = template;
    const { width, height } = certificatePageSize(orientation);
    const starburst = starburstFor(orientation, width);
    return (
      <CentredLayout
        template={template}
        text={text}
        orientation={orientation}
        inset={{
          // Clear of the starburst: beside it on landscape, below it on portrait.
          landscape: "80px 160px 72px",
          portrait: `${starburst.y + starburst.radius + 18}px 92px 84px`,
        }}
        frame={
          <>
            <ShapeBorder
              width={width}
              height={height}
              colours={[primary, accent]}
              keepClearOf={starburst}
            />
            <Box
              sx={{
                position: "absolute",
                inset: BORDER_INSET,
                border: `8px solid ${primary}`,
                borderRadius: "32px",
              }}
            />
            <Box
              sx={{
                position: "absolute",
                inset: BORDER_INSET + 18,
                border: `3px dashed ${accent}`,
                borderRadius: "20px",
              }}
            />
            <Starburst
              {...starburst}
              primary={primary}
              accent={accent}
              paper={certificatePaperColour(template)}
            />
          </>
        }
      />
    );
  },
};
